import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, X } from "lucide-react";

/**
 * Google Places Autocomplete Component
 * Uses the Google Maps Places API for address suggestions
 * 
 * Props:
 * - value: Current address value
 * - onChange: Callback when address is selected (returns full place details)
 * - onInputChange: Callback for raw input text changes
 * - placeholder: Input placeholder text
 * - className: Additional CSS classes
 */
export default function GooglePlacesAutocomplete({
  value = "",
  onChange,
  onInputChange,
  placeholder = "Enter location...",
  className = ""
}) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const inputRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const sessionToken = useRef(null);
  const debounceTimer = useRef(null);
  
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Load Google Maps API
  useEffect(() => {
    if (!apiKey) {
      console.warn("Google Maps API key not configured");
      return;
    }

    // Check if already loaded
    if (window.google?.maps?.places) {
      setIsApiLoaded(true);
      return;
    }

    // Load the script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsApiLoaded(true);
    };
    script.onerror = () => {
      console.error("Failed to load Google Maps API");
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, [apiKey]);

  // Initialize services when API is loaded
  useEffect(() => {
    if (isApiLoaded && window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      // Create a dummy div for PlacesService (required)
      const dummyDiv = document.createElement("div");
      placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, [isApiLoaded]);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Fetch suggestions
  const fetchSuggestions = useCallback((input) => {
    if (!autocompleteService.current || !input.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);

    const request = {
      input: input,
      sessionToken: sessionToken.current,
      types: ["establishment", "geocode"], // Businesses and addresses
    };

    autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
      setIsLoading(false);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        setSuggestions(predictions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
      }
    });
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onInputChange?.(newValue);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce the API call
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    if (!placesService.current) return;

    setIsLoading(true);

    // Get full place details
    placesService.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: ["formatted_address", "geometry", "name", "address_components"],
        sessionToken: sessionToken.current
      },
      (place, status) => {
        setIsLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const fullAddress = place.formatted_address || suggestion.description;
          setInputValue(fullAddress);
          setShowSuggestions(false);
          
          // Extract location details
          const locationData = {
            address: fullAddress,
            name: place.name,
            placeId: suggestion.place_id,
            lat: place.geometry?.location?.lat(),
            lng: place.geometry?.location?.lng(),
            components: {}
          };

          // Parse address components
          place.address_components?.forEach(component => {
            if (component.types.includes("locality")) {
              locationData.components.city = component.long_name;
            }
            if (component.types.includes("administrative_area_level_1")) {
              locationData.components.state = component.short_name;
            }
            if (component.types.includes("country")) {
              locationData.components.country = component.long_name;
            }
            if (component.types.includes("postal_code")) {
              locationData.components.zip = component.long_name;
            }
          });

          onChange?.(locationData);
          
          // Create new session token for next search
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        }
      }
    );
  };

  // Handle clear
  const handleClear = () => {
    setInputValue("");
    setSuggestions([]);
    setShowSuggestions(false);
    onInputChange?.("");
    onChange?.(null);
    inputRef.current?.focus();
  };

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // If no API key, show simple input
  if (!apiKey) {
    return (
      <div className={`relative ${className}`}>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              onInputChange?.(e.target.value);
              onChange?.({ address: e.target.value });
            }}
            placeholder={placeholder}
            className="pl-10 pr-8"
          />
        </div>
        <p className="text-xs text-amber-600 mt-1">Google Maps not configured - enter address manually</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={inputRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue && suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pl-10 pr-8"
          data-testid="location-autocomplete-input"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
        {!isLoading && inputValue && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, idx) => (
            <button
              key={suggestion.place_id}
              onClick={() => handleSelectSuggestion(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-start gap-3 border-b border-gray-100 last:border-0"
              data-testid={`location-suggestion-${idx}`}
            >
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {suggestion.structured_formatting?.main_text || suggestion.description}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {suggestion.structured_formatting?.secondary_text || ""}
                </p>
              </div>
            </button>
          ))}
          <div className="px-4 py-2 text-xs text-gray-400 bg-gray-50">
            Powered by Google
          </div>
        </div>
      )}
    </div>
  );
}
